import { Button } from '@mui/material';
import React, { FC, FormEventHandler, useState } from 'react';

import ComposeMetaTemplate from '../../../../containers/compose/composeMetaTemplate';
import ComposeMetaText from '../../../../containers/compose/composeMetaText';
import ComposeMetaTitle from '../../../../containers/compose/composeMetaTitle';
import { ComposeNodeType } from '../../../../containers/compose/props';
import useParamTopicId from '../../../../hooks/router/params/useParamTopicId';
import useIsMobile from '../../../../hooks/useIsMobile';
import translate from '../../../../i18n/translate';
import { DialogId } from '../../../../models/dialog';
import ConfirmPopover from '../../../MUI/ConfirmPopover';
import MuiIcon from '../../../MUI/MuiIcon';
import ComposeMeta from '../ComposeMeta';

import * as Styled from './styled';

export type Props = {
  formId: string;
  isInitialDraftChanged: boolean;
  nodeType: ComposeNodeType;
  requestClear: () => void;
  requestSubmit: () => void;
  shouldShowTitle?: boolean;
};

/**
 * Main fields for compose (title and description)
 *
 * @param props                       Props passed to the component
 * @param props.formId                The formId, optional argument
 * @param props.isInitialDraftChanged Whether the initial draft has been changed
 * @param props.nodeType              Whether the compose form is being used for messages or templates
 * @param props.requestClear          The callback on compose clear
 * @param props.requestSubmit         The callback on compose submit
 * @param props.shouldShowTitle       Whether to show the title input
 * @returns                           The component itself
 */
const ComposeMain: FC<Props> = ({
  formId,
  isInitialDraftChanged,
  nodeType,
  requestClear,
  requestSubmit,
  shouldShowTitle = false,
}) => {
  const isMobile = useIsMobile();
  const [shouldValidate, setShouldValidate] = useState(false);
  const topicIdUrl = useParamTopicId();
  const topicId = nodeType === 'MESSAGE' ? topicIdUrl : null;

  /**
   * The user has submitted the form,
   * so we request that the message/template is created/updated
   *
   * @param event The event that took place
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    setShouldValidate(true);

    if (event.currentTarget.checkValidity()) {
      requestSubmit();
      setShouldValidate(false);
      if (topicId === null) {
        setShouldValidate(true);
      }
      return;
    }
    if (!isMobile) {
      const invalidInput: HTMLElement | null =
        event.currentTarget.querySelector('input:invalid, textarea:invalid');

      invalidInput?.focus();
    }
  };

  const mainProps = {
    nodeType,
    shouldValidate,
  };

  return (
    <Styled.Form id={formId} noValidate={true} onSubmit={onSubmit}>
      <Styled.FormHeader>
        {nodeType !== 'TEMPLATE' && (
          <ComposeMetaTemplate nodeType={nodeType} topicId={topicId} />
        )}
        <ConfirmPopover
          onConfirm={requestClear}
          popoverProps={{
            anchorOrigin: {
              horizontal: 'right',
              vertical: isMobile ? 'bottom' : 'top',
            },
            transformOrigin: {
              horizontal: 'right',
              vertical: isMobile ? 'top' : 'bottom',
            },
          }}
          trigger={
            <Button
              disabled={!isInitialDraftChanged}
              startIcon={<MuiIcon icon="clear" />}
            >
              {translate('GENERAL__CLEAR')}
            </Button>
          }
          type={DialogId.ComposeClear}
        />
      </Styled.FormHeader>
      <ComposeMeta
        formId={formId}
        nodeType={nodeType}
        position="ABOVE"
        shouldValidate={shouldValidate}
      />
      {shouldShowTitle && <ComposeMetaTitle formId={formId} {...mainProps} />}
      <ComposeMetaText {...mainProps} />
    </Styled.Form>
  );
};

export default ComposeMain;
